// @flow

import React, { useState } from 'react';
import { flatten, get, isEmpty, map, omit, sortBy, toString, without } from 'lodash/fp';
import styled from '@emotion/styled';
import {
  faTrashAlt,
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'app/components/commons/Button';
import Spinner from 'app/components/commons/Spinner';
import StarSymbol from 'app/components/challenge/StarSymbol';
import Rows from 'app/components/layout/Rows';
import t from 'app/utils/translate';
import Title from 'app/components/commons/Title';
import Section from 'app/components/commons/Section';
import { crunchLineUps } from 'app/api/challenges';
import decodeLineup from 'app/normalizers/decodeLineup';
import { mapLineups } from 'app/selectors/myTeamsSelector';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';

import CruncherEntityFilter from './CruncherEntityFilter';
import { ButtonContainer, ButtonLabel, Row, Subtitle } from './components';
import ConfirmModal from '../ConfirmModal';

type Props = {
  cruncherRules: Object,
  darkMode: boolean,
  games: Array<Object>,
  players: Object,
  isDesktop: boolean,
  slateId: number,
  onCrunchSuccess: (lineUps: Array<*>) => void,
  onShowRules: () => void
};

const SuccessIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.color.success,
  marginRight: theme.spacing.small
}));

const LightIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.color.white
}));

const ChevronIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.color.white,
  marginLeft: theme.spacing.small
}));

const StatsLabel = styled.span(({ theme }) => ({
  textTransform: 'uppercase',
  fontWeight: theme.fontWeight.demiBold,
  paddingRight: theme.spacing.small
}));

const RacingCruncherSelections = ({
  cruncherRules,
  darkMode,
  games,
  players,
  isDesktop,
  onCrunchSuccess,
  onShowRules,
  slateId
}: Props) => {
  const [selectedPlayers, setSelectedPlayers] = useState({});
  const [gamesDisabled, setGamesDisabled] = useState([]);
  const [gamesCollapsed, setGamesCollapsed] = useState([]);
  const [action, setAction] = useState(undefined);
  const [error, setError] = useState(undefined);
  const [allLegsOpened, setAllLegsOpened] = useState(true);

  const handleSelectEntity = ({ id, gameId }) => {
    const gamePlayers = get(gameId, selectedPlayers) || [];

    const newGamePlayers = gamePlayers.includes(id)
      ? without([id], gamePlayers)
      : [...gamePlayers, id];

    setSelectedPlayers(
      newGamePlayers.length > 0
        ? { ...selectedPlayers, [gameId]: newGamePlayers }
        : omit(gameId, selectedPlayers)
    );

    if (newGamePlayers.length >= cruncherRules.maxPlayersPerGame) {
      if (!gamesDisabled.includes(gameId)) {
        setGamesDisabled([...gamesDisabled, gameId]);
      }
    } else if (gamesDisabled.includes(gameId)) {
      setGamesDisabled(without([gameId], gamesDisabled));
    }
  };

  const handleClear = () => {
    setSelectedPlayers({});
    setGamesDisabled([]);
    setGamesCollapsed([]);
    setError(undefined);
    setAction(undefined);
    setAllLegsOpened(true);
  };

  const handleToggleGameCollapsed = (gameId: string) =>
    setGamesCollapsed(
      gamesCollapsed.includes(gameId)
        ? without([gameId], gamesCollapsed)
        : [...gamesCollapsed, gameId]
    );

  const handleToggleOpenAllLegs = () => {
    setAllLegsOpened(!allLegsOpened);
    setGamesCollapsed(!allLegsOpened ? [] : map('gameId', games));
  };

  const handleCrunch = () => {
    setError(undefined);
    setAction('crunching');

    crunchLineUps({
      games: selectedPlayers,
      slate_id: slateId
    })
      .then(({ data, meta: { slate } }) => {
        const allPlayers = flatten(
          map(
            g =>
              (g.players || []).map(p => ({ ...p, startTime: new Date(g.start_time).getTime() })),
            slate.games
          )
        );

        const items = (get('crunched_lineups', data) || []).map((playerIds: Array<number>) => {
          const selectionHash = playerIds.join('-');
          return decodeLineup({
            id: selectionHash,
            featured: false,
            selection_hash: selectionHash,
            selection: {
              players: sortBy(
                'startTime',
                playerIds.map(pId => allPlayers.find(p => toString(p.id) === toString(pId)))
              )
            },
            slate,
            sportId: get('competition_type.sport.id', slate),
            sportIcon: get('competition_type.sport.icon', slate)
          });
        });
        onCrunchSuccess(mapLineups(items));
        window.scrollTo(0, 0);
      })
      .catch(({ errors }) => setError(get('message', errors)))
      .finally(() => setAction(undefined));
  };

  const getCombinations = () => {
    let product = 1;
    let playersNum = 0;

    if (isEmpty(selectedPlayers)) {
      return { product: 0, playersNum };
    }

    Object.entries(selectedPlayers).forEach(data => {
      const gamePlayersNum = (get('[1]', data) || []).length;
      product *= gamePlayersNum || 1;
      playersNum += gamePlayersNum;
    });

    return { product, playersNum };
  };

  const { product, playersNum } = getCombinations();

  const showPlayerList = games.length > 0 && !isEmpty(players) && !isEmpty(cruncherRules);

  const gamesSelected = Object.keys(selectedPlayers);

  const canCrunch = () => {
    if (!playersNum || action === 'crunching') {
      return false;
    }

    if (playersNum > cruncherRules.maxPlayersPerSlate) {
      return false;
    }

    if (cruncherRules.minPlayersPerSlate >= 1 && gamesSelected.length !== games.length) {
      return false;
    }

    return true;
  };

  return (
    <div>
      {isDesktop ? (
        <React.Fragment>
          <Title>Select Runners</Title>
          <Subtitle>
            A maximum of {cruncherRules.maxPlayersPerSlate} selections can be made per individual
            calculation. A selection is required in each leg. All points displayed for win and place
            are <StarSymbol>projections</StarSymbol>
          </Subtitle>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Section
            title="Select Runners"
            renderLeftElement={() => (
              <Button kind="bare" onClick={onShowRules}>
                <LightIcon icon={faInfoCircle} size="sm" />
              </Button>
            )}
            renderRightElement={() => (
              <Button kind="bare" onClick={() => setAction('clearing')}>
                <LightIcon icon={faTrashAlt} size="sm" />
              </Button>
            )}
          >
            <Subtitle>
              Min 1 selection per leg, max {cruncherRules.maxPlayersPerSlate} selections. All points
              displayed for win and place are <StarSymbol>projections</StarSymbol>
            </Subtitle>
          </Section>
          <Button block autoBorderRadius={false} kind="info" onClick={handleToggleOpenAllLegs}>
            <div className="buttonInner">
              <ButtonLabel>{allLegsOpened ? 'CLOSE ALL LEGS' : 'OPEN ALL LEGS'}</ButtonLabel>
            </div>
          </Button>
        </React.Fragment>
      )}
      {games.map(({ gameId, name, number = 0 }, i) => (
        <Section
          key={gameId}
          title={`L${i + 1} ${name} R${number}`}
          onTitleClick={() => handleToggleGameCollapsed(gameId)}
          renderRightElement={() => (
            <ButtonContainer>
              {gamesSelected.includes(gameId) && (
                <React.Fragment>
                  <SuccessIcon icon={faCheckCircle} size="lg" />
                  <h4>{selectedPlayers[gameId].length}</h4>
                </React.Fragment>
              )}
              <ChevronIcon
                icon={gamesCollapsed.includes(gameId) ? faChevronDown : faChevronUp}
                size="sm"
              />
            </ButtonContainer>
          )}
        >
          {!gamesCollapsed.includes(gameId) && (
            <Rows desktop={3} mobile={1} tabletPortrait={2} gutter={3}>
              {sortBy('number', get(gameId, players) || []).map(
                ({ id, name: playerName, isScratched, labels, winProjected, placeProjected }) => (
                  <CruncherEntityFilter
                    key={id}
                    name={playerName}
                    win={winProjected}
                    place={placeProjected}
                    disabled={
                      (gamesDisabled.includes(gameId) ||
                        playersNum >= cruncherRules.maxPlayersPerSlate) &&
                      !(get(gameId, selectedPlayers) || []).includes(id)
                    }
                    labels={labels}
                    isScratched={isScratched}
                    isSelected={(get(gameId, selectedPlayers) || []).includes(id)}
                    onSelectEntity={() => handleSelectEntity({ id, gameId })}
                    scheme={darkMode ? 'dark' : 'light'}
                  />
                )
              )}
            </Rows>
          )}
        </Section>
      ))}
      {error && <FormValidationMessage message={error} />}
      {showPlayerList && (
        <React.Fragment>
          <Row justifyContent="center">
            <StatsLabel>
              {isDesktop ? 'Legs Selected' : 'LEGS'}: {gamesSelected.length}/{games.length}
            </StatsLabel>
            <StatsLabel>
              {isDesktop ? 'Combinations' : 'COMBOS'}: {product}
            </StatsLabel>
            <StatsLabel>
              {isDesktop ? 'Selections' : 'SEL'}: {playersNum}
            </StatsLabel>
          </Row>
          <Row>
            * Maximum of 150 combinations can be calculated. If you exceed the maximum, only the
            first 150 lineups will be calculated.
          </Row>
          <Row justifyContent="center" flexDirection={isDesktop ? 'row' : 'column'}>
            <Button
              block={!isDesktop}
              autoBorderRadius={false}
              kind="success"
              disabled={!canCrunch()}
              onClick={handleCrunch}
              style={{ marginBottom: isDesktop ? 0 : 8 }}
            >
              <div className="buttonInner">
                {action === 'crunching' && <Spinner size="small" inlineLeft />}
                <ButtonLabel>
                  {t(`${action === 'crunching' ? 'Calculating...' : 'Calculate'}`)}
                </ButtonLabel>
              </div>
            </Button>
            <Button
              block={!isDesktop}
              autoBorderRadius={false}
              kind="danger"
              disabled={isEmpty(selectedPlayers)}
              onClick={handleClear}
            >
              <div className="buttonInner">
                <ButtonLabel>{t('Start Over')}</ButtonLabel>
              </div>
            </Button>
          </Row>
        </React.Fragment>
      )}
      {action === 'clearing' && (
        <ConfirmModal
          onCancel={() => setAction(undefined)}
          onSubmit={handleClear}
          title="Clear selected horses?"
          label="Clear"
        />
      )}
    </div>
  );
};

export default RacingCruncherSelections;
